import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";

/**
 * Where the panel's two calls to action lead. `/` is the sign-in landing and `/signup` the sign-up
 * form (routes.tsx). Exported so the recipient shell and the design-viewer header — the two places
 * this panel is mounted — cannot drift to different destinations.
 */
export const SHARE_SIGN_IN_HREF = "/";
export const SHARE_SIGN_UP_HREF = "/signup";

/**
 * The recipient's "Viewing as guest" panel (PRD 4.6).
 *
 * Sits top-right on every recipient screen and is the ONLY marketing surface in the whole recipient
 * flow — which is also the constraint on it: it says what CUBE is and offers a sign-up, and it must
 * never present itself as navigation into the portal the recipient is looking at. A guest has no
 * account and no project; anything that implied otherwise would be a dead end.
 *
 * The avatar is deliberately blank and dashed. A filled avatar reads as "you are signed in", which is
 * the opposite of what this communicates.
 */
type GuestAccountPanelProps = {
  /** Where "Create CUBE account" and "Sign in" send the recipient. */
  signUpHref: string;
  signInHref: string;
};

function GuestAccountPanel({ signUpHref, signInHref }: Readonly<GuestAccountPanelProps>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Close on an outside click or Escape.
   *
   * Bound on the document rather than via a full-screen scrim: the panel overlays a viewer whose
   * canvas owns mouse capture, and a scrim would swallow the drag events the recipient needs for
   * orbit and pan even while the panel is shut.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (event.target instanceof Node && containerRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("Account")}
        aria-expanded={open}
        title={t("Viewing as guest")}
        className="grid h-9 w-9 place-items-center rounded-full border border-dashed border-stroke_primary bg-white"
      >
        <IconComponent name="UserProfile" width="1.125rem" height="1.125rem" />
      </button>

      {open && (
        <div className="absolute end-0 top-11 z-50 w-80 overflow-hidden rounded-lg border border-stroke_primary bg-white shadow-lg">
          <div className="flex items-center gap-3 border-b border-stroke_primary bg-selection_primary px-4 py-3">
            <div className="grid h-9 w-9 flex-none place-items-center rounded-full border border-dashed border-stroke_primary bg-white">
              <IconComponent name="UserProfile" width="1.125rem" height="1.125rem" />
            </div>
            <div className="min-w-0">
              <Text className="block text-sm font-semibold">{t("Viewing as guest")}</Text>
              <Text className="block text-xs text-text_light">{t("You are not signed in to CUBE")}</Text>
            </div>
          </div>

          <div className="flex flex-col gap-3 px-4 py-4">
            <Text className="text-sm font-semibold">{t("Do more with CUBE")}</Text>
            <div className="flex gap-2 text-xs leading-relaxed text-text_light">
              <IconComponent name="Transmittals" width="1rem" height="1rem" />
              <Text>{t("Share your own files and folders with anyone, inside or outside your organisation.")}</Text>
            </div>
            <div className="flex gap-2 text-xs leading-relaxed text-text_light">
              <IconComponent name="issues" width="1rem" height="1rem" />
              <Text>{t("Create issues and markups on the files shared with you.")}</Text>
            </div>

            {/* Full page loads, not SPA navigation: these leave the share context entirely, and the
                recipient's session state here is nothing worth preserving. */}
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={() => {
                globalThis.location.href = signUpHref;
              }}
            >
              {t("Create CUBE account")}
            </ButtonComponent>
            <Text className="text-center text-xs text-text_light">
              {t("Already have an account?")}{" "}
              <a href={signInHref} className="font-semibold text-primary_text">
                {t("Sign in")}
              </a>
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuestAccountPanel;
